import {
    UitkCard,
    UitkCardContentSection,
    UitkCardFeaturedHeader,
    UitkLayoutFlex,
    UitkLayoutFlexItem,
    UitkLoader,
    UitkPrimaryButton,
    UitkSpacing,
    UitkText,
    UitkIcon,
    UitkIconSize,
    UitkLink,
    UitkToast,
    UitkList,
    UitkUnformattedHtml,
    UitkExternalButton,
    egenciaFetch,
    UitkListItem,
    UitkSpacingHr,
    useViewportSize,
    UitkTextInput
} from "@egencia/uitk-react";
import * as React from "react";

export function LLMCard() {
    let loadingTimeout = null
    const [searchValue, setSearchValue] = React.useState("");
    const [searchedValue, setSearchedValue] = React.useState("");
    const [showCard, setShowCard] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [loadingMessage, setLoadingMessage] = React.useState("Looking into articles");
    const [answer, setAnswer] = React.useState('')
    const [docs, setHelpCenterArticles] = React.useState([])
    const [searchError, setSearchError] = React.useState('')
    const errorCodes = ["SUCCESS_NO_OUTPUT", "ERROR_GUARDRAILS_INPUT", "ERROR_GUARDRAILS_OUTPUT", "ERROR_POS_UNSUPPORTED"]
    
    async function callApi() {
        const url = '/help-center/online-inference-llm-response';
        const body = {
            "input": searchValue,
            "context": {
                "locale": EG.currentLocale.replace('-', '_'),
                "product_id": EG.user.productId,
                "role": EG.bootstrapped.cmsUserRoles
            },
            "session_id": EG.sessionId
        };

        try {
             const response = await egenciaFetch(url, {
                method: 'POST',
                body: JSON.stringify(body),
                headers: {
                    "Content-Type": "application/json"
                }
            }).then(r => r.json())
            
            if (errorCodes.includes(response.code)) {
                setAnswer(uitk.i18n.msg('llm_error_from_model'))
            } else {
                setAnswer(response.response?.trim())
                setHelpCenterArticles(response.docs)
            }
            
            setLoading(false)

            setAnswerAnalytics(response)
        } catch (error) {
            setAnswer(null)
            setLoading(false)

            setAnswerAnalytics(null)
        }

        clearTimeout(loadingTimeout)
    }

    function setAnswerAnalytics(response) {
        const page = EG.bootstrapped.isPanel ? 'Panel' : 'HomePage'
        
        if (!response) {
            const errorAnalytics = `HelpCenter.${page}.AISearchResults.Model.Results.Failed`

            uitk.analytics.set('prop18', errorAnalytics);
            uitk.analytics.set('eVar26', errorAnalytics);
            uitk.analytics.track('o', {
                linkName: 'AI Help Center Model Response Failed',
                linkTrackVars: ',prop18,eVar26',
            });
            
            return;
        }
        
        let confidence = 'Unavailable'

        const answerAnalytics = `HelpCenter.${page}.AISearchResults.${confidence}.Confidence.Displayed`

        uitk.analytics.set('prop18', answerAnalytics);
        uitk.analytics.set('eVar26', answerAnalytics);
        uitk.analytics.track('o', {
            linkName: 'AI Help Center Response Displayed',
            linkTrackVars: ',prop18,eVar26',
        });
    }
    
    function validateSearchInput(searchValue) {
        if (searchValue.trim().length === 0) {
            setSearchError(uitk.i18n.msg('llm_search_error'))
            return false;
        }
        
        setSearchError('')
        return true;
    }

    function onSubmit() {
        const isValidInput = validateSearchInput(searchValue);
        
        if (isValidInput) {
            if (EG.bootstrapped.isPanel) {
                omnitureLog.logClickEvent("HelpCenter.Panel.AISearch.Clicked");
            } else {
                omnitureLog.logClickEvent("HelpCenter.HomePage.AISearch.Clicked");
            }

            setShowCard(true);
            setSearchedValue(searchValue);
            setLoading(true);
            setLoadingMessage(uitk.i18n.msg('llm_loading'));

            callApi()
        } else {
            setShowCard(false);
        }
    }
    
    return (
        <UitkLayoutFlex direction={"column"} className={"llm-card"} gap={"four"}>
            <SearchInput onSubmit={onSubmit} value={searchValue} onChange={setSearchValue} searchError={searchError}/>
            {showCard &&
                <UitkCard border>
                    <UitkCardFeaturedHeader title={{ tag: "h3", text: uitk.i18n.msg('llm_ai_answer') }} />
                    <UitkCardContentSection>
                        {loading ? (
                            <QuestionLoading loadingMessage={loadingMessage}/>
                        ) : (
                            (answer !== null) ? (
                                <AnswerDisplay searchValue={searchedValue} answer={answer} docs={docs}/>
                            ) : (
                                <FailureDisplay/>
                            )
                        )}
                    </UitkCardContentSection>
                </UitkCard>
            }
        </UitkLayoutFlex>
    )
}

function SearchInput({ onSubmit, value, onChange, searchError }) {
    const viewport = useViewportSize()
    
    function onKeyUp(e) {
        if (e.key === 'Enter') {
            onSubmit();
        }
    }

    return (
        <div>
            <UitkLayoutFlex space={"two"} direction={viewport.viewportSize === "SMALL" ? "column" : "row"} wrap={"wrap"}>
                <UitkLayoutFlexItem grow={1}>
                    <UitkTextInput
                        id={"llm-search-input"}
                        icon="search"
                        label={uitk.i18n.msg('llm_search_box_placeholder')}
                        onKeyUp={onKeyUp}
                        onChange={(e) => onChange(e.target.value)}
                        value={value}
                        hasClearButton={true}
                        handleClear={() => onChange('')}
                        maxLength={255}
                    />
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                    <UitkPrimaryButton onClick={onSubmit}>
                        <UitkIcon name="cleanliness" size={UitkIconSize.SMALL} />
                        {uitk.i18n.msg('llm_ai_button_text')}
                    </UitkPrimaryButton>
                </UitkLayoutFlexItem>
            </UitkLayoutFlex>
            {searchError?.length > 0 && 
                <UitkText size={200} spacing="one" theme="negative">{searchError}</UitkText>
            }
        </div>
    )
}

function QuestionLoading({ loadingMessage }) {
    return (
        <UitkLayoutFlex alignItems={"center"}>
            <UitkLayoutFlexItem>
                <UitkSpacing margin={{inlineend: "two"}}>
                    <UitkLoader isSmall={true}/>
                </UitkSpacing>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem grow={1}>
                <UitkText>{loadingMessage}</UitkText>
            </UitkLayoutFlexItem>
        </UitkLayoutFlex>
    )
}

function AnswerDisplay({ searchValue, answer, docs }) {
    return (
        <div>
            <QuestionDisplay searchValue={searchValue}/>
            <UitkSpacing margin={{"blockstart": "four"}}>
                <UitkLayoutFlex alignItems={"center"} space={"two"}>
                    <UitkLayoutFlexItem alignSelf={"start"}>
                            <UitkIcon className={"llm-icon"} name="cleanliness" size={UitkIconSize.SMALL} />
                    </UitkLayoutFlexItem>
                    <UitkLayoutFlexItem>
                        <AnswerText answer={answer}/>
                    </UitkLayoutFlexItem>
                </UitkLayoutFlex>
            </UitkSpacing>
            <UitkSpacing margin={{"blockstart": "four"}}>
                <HelpCenterArticles docs={docs}/>
                <AnswerFeedback searchValue={searchValue} />
                <DisclaimerMessage />
            </UitkSpacing>
        </div>
    )
}

function FailureDisplay() {
    return (
        <UitkLayoutFlex alignItems={"center"} space={"two"}>
            <UitkLayoutFlexItem alignSelf={"start"}>
                <UitkIcon className={"llm-failed-icon"} name="clear" theme="negative" size={UitkIconSize.SMALL} />
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
                <AnswerText answer={uitk.i18n.msg('llm_model_failure')}/>
            </UitkLayoutFlexItem>
        </UitkLayoutFlex>
    )
}

function QuestionDisplay({ searchValue }) {
    return (
        <UitkLayoutFlex alignItems={"center"} space={"two"}>
            <UitkLayoutFlexItem>
                <div className={"success"}>
                    <UitkIcon name="done" size={UitkIconSize.SMALL} />
                </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem>
                <UitkText>{uitk.i18n.msg('llm_showing_answer')} <b>{searchValue}</b></UitkText>
            </UitkLayoutFlexItem>
        </UitkLayoutFlex>
    )
}

function AnswerText({ answer }) {
    const markdownLinkRegex = new RegExp(/\[([^\]]+)\]\(([^\)]+)\)/g)
    
    // Replace all newlines with html br tags
    answer = answer.replaceAll('\n', '<br>')
    
    // Replace all markdown links in format [Title](link reference) with hrefs
    const markdownLinks = answer.match(markdownLinkRegex)
    if (markdownLinks !== null && markdownLinks.length > 0) {
        answer = answer.replaceAll(markdownLinkRegex, '<a href="$2" target="_blank">$1</a>');
    }
    
    return (
        // We need to wrap the entire answer in a <p> for formatting
        <div>
            <UitkUnformattedHtml htmlToParse={'<p>' + answer + '</p>'}/>
        </div>
    )
}

function HelpCenterArticles({ docs }) {
    // Do not display articles and header text if we received none
    if (!docs || docs.length === 0) {
        return "";
    }
    
    const helpCenterArticleLinks = docs.map((d, index) => {
        return (
            <UitkSpacing key={index} margin={{inlinestart: "six", blockstart: "two"}}>
                <UitkLayoutFlex>
                    <UitkLayoutFlexItem>
                        <UitkListItem>
                            <AnswerSource docIndex={d.index} docTitle={d.title}/>
                        </UitkListItem>
                    </UitkLayoutFlexItem>
                </UitkLayoutFlex>
            </UitkSpacing>
        )
    });
    
    return (
        <div>
            <UitkSpacing margin={{inlinestart: "six"}}>
                <UitkText weight={"bold"}>{uitk.i18n.msg('llm_according_to')}</UitkText>
            </UitkSpacing>
            <UitkSpacing margin={{blockstart: "two"}}>
                <UitkList bullet={"no-bullet"}>
                    {helpCenterArticleLinks}
                </UitkList>
            </UitkSpacing>
        </div>
    )
}

function AnswerSource({ className, docTitle, docIndex }) {
    return (
        <span className={className}>
            <UitkLink onClick={() => onArticleClicked(docIndex)} href={'/help-center/article/' + docIndex}>
                <a target="_blank"><UitkIcon name={"open_in_new"} size={UitkIconSize.SMALL}/>{docTitle}</a>
            </UitkLink>
        </span>
    )
}

function onArticleClicked(docIndex) {
    const page = EG.bootstrapped.isPanel ? 'Panel' : 'HomePage'
    omnitureLog.logClickEvent(`HelpCenter.${page}.AISearchResults.Article.${docIndex}.Clicked`);
}

function AnswerFeedback({ searchValue }) {
    const [isThumbsUpClicked, setIsThumbsUpClicked] = React.useState(false);
    const [isThumbsDownClicked, setIsThumbsDownClicked] = React.useState(false);
    const [showFeedbackToast, setShowFeedbackToast] = React.useState(false);

    function onFeedback(isPositive) {
        // Log event so we can compare feedback with search
        uitk.logger.info(`Feedback received: ${isPositive ? "Positive" : "Negative"}, Role(s): ${EG.bootstrapped.cmsUserRoles}, POS: ${EG.user.productId}, TUID: ${EG.user.userId}, GPID: ${EG.user.companyId}, Locale: ${EG.currentLocale}, Search: ${searchValue}`)

        if (isPositive) {
            if (EG.bootstrapped.isPanel) {
                omnitureLog.logClickEvent("HelpCenter.Panel.AISearchResults.ThumbsUp.Clicked");
            } else {
                omnitureLog.logClickEvent("HelpCenter.HomePage.AISearchResults.ThumbsUp.Clicked");
            }
            
            setIsThumbsUpClicked(true);
            setIsThumbsDownClicked(false);
            setShowFeedbackToast(true);
        } else {
            if (EG.bootstrapped.isPanel) {
                omnitureLog.logClickEvent("HelpCenter.Panel.AISearchResults.ThumbsDown.Clicked");
            } else {
                omnitureLog.logClickEvent("HelpCenter.HomePage.AISearchResults.ThumbsDown.Clicked");
            }
            
            setIsThumbsDownClicked(true);
            setIsThumbsUpClicked(false);
            setShowFeedbackToast(true);
        }
    }

    return (
        <div>
            <UitkSpacing padding={{small: { blockstart: "four", inline: "six" }, medium: { blockstart: "two", inline: "six" }}}>
                <div>
                    <UitkLayoutFlex wrap={"wrap"} alignItems={"center"}>
                        <UitkLayoutFlexItem>
                            <UitkText>{uitk.i18n.msg('llm_feedback')}</UitkText>
                        </UitkLayoutFlexItem>
                        <UitkLayoutFlexItem>
                            <UitkSpacing padding={{medium: { inlinestart: "four" }}}>
                                <div>
                                    <UitkExternalButton
                                        className={"thumb-button"}
                                        onClick={() => onFeedback(true)}
                                        aria-label={"positive feedback button"}
                                        aria-pressed={isThumbsUpClicked}
                                    >
                                        <UitkIcon
                                            className={isThumbsUpClicked ? "thumb-icon-clicked" : "thumb-icon"}
                                            name={"thumb_up"}
                                            size={"small"}
                                        />
                                    </UitkExternalButton>
                                    <UitkExternalButton
                                        onClick={() => onFeedback(false)}
                                        aria-label={"negative feedback button"}
                                        aria-pressed={isThumbsDownClicked}
                                    >
                                        <UitkIcon
                                            className={isThumbsDownClicked ? "thumb-icon-clicked" : "thumb-icon"}
                                            name={"thumb_down"}
                                            size={"small"}
                                        />
                                    </UitkExternalButton>
                                </div>
                            </UitkSpacing>
                        </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                </div>
            </UitkSpacing>
            <UitkToast show={showFeedbackToast} onDismiss={() => setShowFeedbackToast(false)}> {uitk.i18n.msg('llm_feedback_thank_you')} </UitkToast>
        </div>
    )
}

function DisclaimerMessage() {
    return (
        <div>
            <UitkSpacingHr/>
            <UitkLayoutFlex alignItems={"center"} space={"two"}>
                <UitkLayoutFlexItem>
                    <UitkIcon name="info_outline" size={UitkIconSize.SMALL} />
                </UitkLayoutFlexItem>
                <UitkLayoutFlexItem>
                    <UitkText>{uitk.i18n.msg('llm_disclaimer')}</UitkText>
                </UitkLayoutFlexItem>
            </UitkLayoutFlex>
        </div>
    )
}